import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';
import type { TopicCombination } from '../models';

export interface WritingTaskSubmitItem {
  prompt: string;
  originalText: string;
  wordCount: number;
}

export interface WritingExamSubmitRequest {
  combinationId: string;
  tasks: WritingTaskSubmitItem[];
  ui_language?: string;
}

export interface WritingExamRecord {
  userId: string;
  combinationId: string;
  createdAt: string;
  lastUpdatedAt: string;
  overallNclc: number | null;
  tasks: Record<
    string,
    {
      status: 'graded' | 'uncompleted';
      prompt: string;
      originalText: string;
      wordCount: number;
      aiResult: AiResult | null;
    }
  >;
}

export interface AiResult {
  scores: {
    nclc_level: number;
    cefr_level: string;
    word_count: number;
    word_count_valid: boolean;
  };
  corrected_text: string;
  feedback: {
    task_fulfillment: string;
    grammar_errors: Array<{
      original: string;
      correction: string;
      explanation: string;
    }>;
    vocabulary_upgrades: Array<{
      original_word: string;
      better_alternative: string;
      reason: string;
    }>;
    connectors_used: string[];
    missing_connectors: string[];
    high_scoring_patterns_found: Array<{
      excerpt: string;
      pattern_name: string;
      explanation: string;
    }>;
    suggested_patterns: Array<{
      pattern_name: string;
      original_sentence: string;
      improved_sentence: string;
      explanation: string;
    }>;
  };
}

export interface WritingHistoryListResponse {
  items: WritingExamRecord[];
  count: number;
  lastKey: string | null;
}

@Injectable({ providedIn: 'root' })
export class WritingHistoryService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/writing`;

  /**
   * Submit a writing exam. Grades tasks via API and returns the full record.
   */
  async submitExam(
    combination: TopicCombination,
    answers: string[],
    uiLanguage = '简体中文'
  ): Promise<WritingExamRecord> {
    const combinationId = `${combination.year}-${combination.month.toString().padStart(2, '0')}-${combination.combinationNumber}`;

    const tasks: WritingTaskSubmitItem[] = [
      { prompt: combination.tache1.prompt, originalText: answers[0] ?? '', wordCount: this.countWords(answers[0]) },
      { prompt: combination.tache2.prompt, originalText: answers[1] ?? '', wordCount: this.countWords(answers[1]) },
      {
        prompt: this.buildTask3Prompt(combination),
        originalText: answers[2] ?? '',
        wordCount: this.countWords(answers[2]),
      },
    ];

    const body: WritingExamSubmitRequest = {
      combinationId,
      tasks,
      ui_language: uiLanguage,
    };

    const record = await firstValueFrom(
      this.http.post<WritingExamRecord>(`${this.apiUrl}/submit`, body)
    );
    return record;
  }

  async checkEligibility(): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.get<{ eligible: boolean }>(`${this.apiUrl}/eligibility`)
    );
    return response.eligible;
  }

  async listHistory(limit = 20, lastKey?: string | null): Promise<WritingHistoryListResponse> {
    const params: Record<string, string | number> = { limit };
    if (lastKey) params['lastKey'] = lastKey;

    return firstValueFrom(
      this.http.get<WritingHistoryListResponse>(`${this.apiUrl}`, { params })
    );
  }

  async getByCombinationId(combinationId: string): Promise<WritingExamRecord> {
    return firstValueFrom(
      this.http.get<WritingExamRecord>(`${this.apiUrl}/${encodeURIComponent(combinationId)}`)
    );
  }

  private countWords(text: string | undefined): number {
    if (!text?.trim()) return 0;
    return text.trim().split(/\s+/).length;
  }

  private buildTask3Prompt(c: TopicCombination): string {
    if (!c.tache3) return '';
    let prompt = c.tache3.prompt;
    if ('document1' in c.tache3 && c.tache3.document1) {
      prompt += `\n\nDocument 1: ${c.tache3.document1}`;
      if ('document2' in c.tache3 && c.tache3.document2) {
        prompt += `\n\nDocument 2: ${c.tache3.document2}`;
      }
    }
    return prompt;
  }
}
